import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import EnableProfileModal from '@components/EnableProfileModal';
import CheckBoxField from '@components/Form/CheckBoxField';
import MultiValueAutocompleteField from '@components/Form/MultiValueAutocompleteField';
import TextAreaField from '@components/Form/TextAreaField';
import TextField from '@components/Form/TextField';
import {
  useCurrentUser,
  useEntitiesTop,
  useEventTracking,
  useForumPostAdminUpdate,
  useForumPostCreate,
  useForumPostUpdate,
} from '@hooks';
import { forum_post_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { MATCH_EXACT, autocompleteEntities, createEntity } from '@utils/backend-api/entities';
import {
  FORUM_POSTS_KEY,
  ForumPost,
  NewForumPost as NewForumPostType,
} from '@utils/backend-api/forumPosts';
import {
  MAX_POLL_OPTIONS,
  MIN_POLL_OPTIONS,
  Poll,
  canEditPoll,
  canRemovePoll,
  emptyPollAttributes,
  isPollExpirationInAllowedWindow,
} from '@utils/backend-api/polls';
import { ENTITY_INPUT_SEARCH_THRESHOLD } from '@utils/constants';
import { stripHtml } from '@utils/formatting';
import { appendSchemeToUrlString, optionalSchemeURLRegex } from '@utils/url_validations';

import AnonymousPostBlock from './AnonymousPostBlock';
import PollFieldsGroup from './PollFieldsGroup';
import styles from './styles.module.scss';

const pollAttributesSchema = (existingExpiresAt?: string) =>
  Yup.object({
    question: Yup.string().required('A poll question is required'),
    expires_at: Yup.string()
      .required('A poll expiration is required')
      .test('expiration-window', 'Poll expiration must be within 7 days from now', (value) => {
        if (!value) return false;
        if (existingExpiresAt && value === existingExpiresAt) return true;
        return isPollExpirationInAllowedWindow(value);
      }),
    poll_options_attributes: Yup.array()
      .of(
        Yup.object({
          text: Yup.string().test('option-text', 'Option text is required', function (value) {
            if (this.parent._destroy) return true;
            return !!value;
          }),
          _destroy: Yup.boolean(),
        })
      )
      .test(
        'visible-option-count',
        `At least ${MIN_POLL_OPTIONS} options are required`,
        (options) =>
          (options || []).filter((option) => !option?._destroy).length >= MIN_POLL_OPTIONS
      )
      .test(
        'visible-option-max',
        `At most ${MAX_POLL_OPTIONS} options are allowed`,
        (options) =>
          (options || []).filter((option) => !option?._destroy).length <= MAX_POLL_OPTIONS
      ),
  });

const validationSchema = (isPoll: boolean, existingExpiresAt?: string) => {
  const pollSchema = pollAttributesSchema(existingExpiresAt);
  const pollAttributesWhenPresent = Yup.mixed().when('remove_poll', {
    is: true,
    then: Yup.mixed().nullable().notRequired(),
    otherwise: isPoll
      ? pollSchema
      : Yup.lazy((value) => (value == null ? Yup.mixed().nullable().notRequired() : pollSchema)),
  });

  return isPoll
    ? Yup.object({
        poll_attributes: pollAttributesWhenPresent,
      })
    : Yup.object({
        title: Yup.string().required('A title is required'),
        body: Yup.string().required('A post is required'),
        external_url: Yup.string().matches(optionalSchemeURLRegex, 'The url must be valid'),
        poll_attributes: pollAttributesWhenPresent,
      });
};

export const QUICK_TAG_JOBS = 'jobs';
export const QUICK_TAG_FUNDING = 'funding';
export const QUICK_TAG_LAUNCH = 'launch';

export default function NewForumPostForm({
  initialValues,
  onCreateSuccess,
  isManaging,
  isPoll = false,
  existingPoll,
}: {
  initialValues: NewForumPostType;
  onCreateSuccess: (forumPost: ForumPost) => void;
  isManaging: boolean;
  isPoll?: boolean;
  existingPoll?: Poll;
}) {
  const { currentUser } = useCurrentUser();
  const firstName = currentUser?.name?.split(' ')[0] || 'You';
  const isEditing = !!initialValues.id;
  const { mutateAsync: updatePostAsync } = useForumPostUpdate();
  const { mutateAsync: updateAdminPostAsync } = useForumPostAdminUpdate();
  const { mutateAsync: createPostAsync } = useForumPostCreate();
  const { data: topEntityOptions, isLoading: isLoadingTopEntityOptions } = useEntitiesTop();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canComment = currentUser?.permitted_to_comment && currentUser?.can_actually_comment;
  const [canInitiallyComment] = useState(canComment);
  const buttonText = isEditing ? 'Update' : `Post as ${firstName}`;
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.NewForumPost,
  });

  initialValues.body = stripHtml(initialValues.body);

  const onQuickTagClick = async (tagName, values, setFieldValue) => {
    const entities = await autocompleteEntities({
      fragment: tagName,
      relationship_sources: ['chat_gpt', 'user_action'],
      order_entities_by: ['is_important', 'pdl_verified'],
      match_type: MATCH_EXACT,
    });
    if (entities.length === 0) return; // this should never happen
    if (values.entity_tags.map((entity) => entity.value).includes(entities[0].value)) {
      setFieldValue(
        'entity_tags',
        values.entity_tags.filter((entity) => entity.value !== entities[0].value)
      );
      trackEvent(click({ clickTarget: ClickTarget.QuickTag, clickText: `Remove: ${tagName}` }));
    } else {
      setFieldValue('entity_tags', [...values.entity_tags, entities[0]]);
      trackEvent(click({ clickTarget: ClickTarget.QuickTag, clickText: `Add: ${tagName}` }));
    }
  };

  const isStaff = !!currentUser?.administrator?.is_active;
  const canEditExistingPoll = canEditPoll(existingPoll, { isStaff });

  const handleSubmit = async (values) => {
    const data: NewForumPostType = {
      ...values,
      // ForumPost.title is required, so a standalone poll's question doubles as its title
      title: isPoll ? values.poll_attributes?.question : values.title,
      body: isPoll ? values.poll_attributes?.question : values.body,
      external_url: isPoll ? undefined : appendSchemeToUrlString(values.external_url),
      entity_tags: values?.entity_tags.map((entity) => entity.value),
    };

    if (values.remove_poll || (isEditing && existingPoll && !canEditExistingPoll)) {
      delete data.poll_attributes;
    }
    if (!isEditing) {
      delete data.remove_poll;
    }

    trackEvent(click({ clickTarget: ClickTarget.SubmitButton }));

    if (isEditing) {
      const updateAction = isManaging ? updateAdminPostAsync : updatePostAsync;
      return updateAction(data, {
        onSuccess: (data: ForumPost) => {
          toast.success('Post has been updated!');
          queryClient.invalidateQueries(FORUM_POSTS_KEY);
          navigate(forum_post_path(data.id));
          window.scrollTo(0, 0);
        },
        onError: (e) => {
          console.error(e);
          toast.error('Post was not updated, please try again.');
        },
      });
    }

    return createPostAsync(data, {
      onSuccess: (data: ForumPost) => {
        queryClient.invalidateQueries(FORUM_POSTS_KEY);
        onCreateSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        toast.error('Post was not created, please try again.');
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={() => validationSchema(isPoll, initialValues.poll_attributes?.expires_at)}
      enableReinitialize={true}
    >
      {({ isSubmitting, submitForm, setFieldValue, values }) => {
        const canAddPoll = !isPoll && !existingPoll;
        const hasPoll = !!values.poll_attributes;
        const showExistingPoll = isEditing && !!existingPoll && !values.remove_poll;
        const canRemoveExistingPoll = canRemovePoll(existingPoll);
        const pollFieldsDisabled = !!existingPoll && !canEditExistingPoll;

        const handleTogglePoll = () => {
          setFieldValue('poll_attributes', hasPoll ? undefined : emptyPollAttributes());
        };

        const removePollControls = showExistingPoll && canRemoveExistingPoll && (
          <button
            type="button"
            className={clsx('unstyled', styles.removePollButton)}
            onClick={() => setFieldValue('remove_poll', true)}
          >
            Remove poll
          </button>
        );

        const undoRemovePoll = isEditing && existingPoll && values.remove_poll && (
          <div className={styles.pollSection}>
            <div className={styles.pollSectionHeader}>
              <div className="font-suisse-intl-mono text-overline-md text-primary-magenta">
                Poll will be removed
              </div>
              <button
                type="button"
                className={clsx('unstyled', styles.removePollButton)}
                onClick={() => setFieldValue('remove_poll', false)}
              >
                Undo
              </button>
            </div>
          </div>
        );

        return (
          <Form className="form forum-post-form">
            {isPoll ? (
              <>
                {showExistingPoll && (
                  <div className={styles.pollSectionHeader}>
                    <div />
                    {removePollControls}
                  </div>
                )}
                {undoRemovePoll}
                {!values.remove_poll && <PollFieldsGroup disabled={pollFieldsDisabled} />}
              </>
            ) : (
              <>
                <TextField className="mb-24" name="title" label="Title" placeholder="Type Title" />
                <TextAreaField
                  name="body"
                  className="mb-24"
                  label="Post"
                  autoComplete="off"
                  placeholder="Type Text"
                  rows={6}
                />
                {canAddPoll &&
                  (hasPoll ? (
                    <div className={styles.pollSection}>
                      <div className={styles.pollSectionHeader}>
                        <div className="font-suisse-intl-mono text-overline-md text-primary-magenta">
                          Poll
                        </div>
                        <button
                          type="button"
                          className={clsx('unstyled', styles.removePollButton)}
                          onClick={handleTogglePoll}
                        >
                          Remove poll
                        </button>
                      </div>
                      <PollFieldsGroup showTags={false} />
                    </div>
                  ) : (
                    <TextButton
                      as="button"
                      type="button"
                      className={clsx('unstyled', styles.addPollButton)}
                      onClick={handleTogglePoll}
                    >
                      + Add a poll
                    </TextButton>
                  ))}
                {showExistingPoll && (
                  <div className={styles.pollSection}>
                    <div className={styles.pollSectionHeader}>
                      <div className="font-suisse-intl-mono text-overline-md text-primary-magenta">
                        Poll
                      </div>
                      {removePollControls}
                    </div>
                    <PollFieldsGroup showTags={false} disabled={pollFieldsDisabled} />
                  </div>
                )}
                {undoRemovePoll}
                {!isManaging && (
                  <TextField
                    className="mb-24"
                    name="external_url"
                    placeholder="Type URL (Optional)"
                    label="Link"
                  />
                )}
                <div className="TextField form-group">
                  <label className="input-label" htmlFor={'Tags'}>
                    Tags
                  </label>
                  <div className="w-full">
                    <CheckBoxField
                      name="job_tag"
                      label="Job Posting (#jobs)"
                      className={styles.firstCheckboxGroup}
                      onClick={() => onQuickTagClick(QUICK_TAG_JOBS, values, setFieldValue)}
                    />
                    <CheckBoxField
                      name="funding_tag"
                      label="Funding Announcement (#funding)"
                      onClick={() => onQuickTagClick(QUICK_TAG_FUNDING, values, setFieldValue)}
                    />
                    <CheckBoxField
                      name="product_tag"
                      label="Product Launch (#launch)"
                      onClick={() => onQuickTagClick(QUICK_TAG_LAUNCH, values, setFieldValue)}
                    />
                    <MultiValueAutocompleteField
                      placeholder="Type tags"
                      name="entity_tags"
                      fetchMethod={(value) =>
                        autocompleteEntities({
                          fragment: value,
                          relationship_sources: ['chat_gpt', 'user_action'],
                          order_entities_by: ['is_important', 'pdl_verified'],
                        })
                      }
                      createMethod={(value) =>
                        createEntity({
                          name: value,
                          entity_type: 'other',
                          word_type: 'common',
                        })
                      }
                      defaultOptions={isLoadingTopEntityOptions ? [] : topEntityOptions}
                      inputLengthFetchThreshold={ENTITY_INPUT_SEARCH_THRESHOLD}
                    />
                  </div>
                </div>
              </>
            )}

            <div className={clsx(styles.formActions)}>
              <div className={styles.formActionsDigest}>
                {(!isEditing || isManaging) && (
                  <CheckBoxField
                    name="email_digest_enabled"
                    label="Include in daily forum post email digest"
                    className="mt0"
                  />
                )}
              </div>

              {canComment && (
                <div className="flex flex-col items-end">
                  <PrimaryButton
                    as="button"
                    type="submit"
                    className="self-end"
                    disabled={isSubmitting}
                    fullWidth
                  >
                    {values.is_anonymous ? 'Post anonymously' : buttonText}
                  </PrimaryButton>
                  {!isEditing && (
                    <AnonymousPostBlock isAnonymous={values.is_anonymous} justify="right" />
                  )}
                </div>
              )}
              {!canInitiallyComment && (
                <div className={canComment ? '!hidden' : ''}>
                  {!currentUser?.permitted_to_comment ? (
                    'Missing name / username, or not permitted to comment'
                  ) : (
                    <EnableProfileModal
                      isLoggedIn={!!currentUser}
                      isSubscriber={currentUser?.is_subscriber}
                      isTriggeredByComment={true}
                      buttonClasses={clsx(styles.submitButton, 'button button-red button-small')}
                      commentButtonText={buttonText}
                      isCommentForForum={true}
                      handleSubmitComment={submitForm}
                    />
                  )}
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
